/**
 * Slack Workspace Connection Schemas
 *
 * Endpoints for connecting/managing Slack workspaces.
 * Workspace connections store tokens for auto-creating bots.
 *
 * Users only need to provide the refresh token - we derive the access token.
 */

import { z } from "zod";

// =============================================================================
// CONNECT WORKSPACE
// =============================================================================

/**
 * Body for connecting a Slack workspace
 *
 * Only refresh token is required - access token is derived automatically.
 */
export const ConnectSlackWorkspaceBodySchema = z.object({
  /** Refresh token (xoxe-1-...) - we derive access token from this */
  refreshToken: z.string().min(1, "Refresh token is required")
});

/**
 * Response from connecting a Slack workspace
 */
export const ConnectSlackWorkspaceResponseSchema = z.object({
  /** Created/updated connection ID */
  connectionId: z.string(),
  /** Slack workspace ID */
  teamId: z.string(),
  /** Slack workspace name */
  teamName: z.string(),
  /** Whether this is an Enterprise Grid workspace */
  isEnterprise: z.boolean(),
  /** Whether this is a new connection or update */
  isNew: z.boolean()
});

// =============================================================================
// LIST WORKSPACES
// =============================================================================

/**
 * Workspace info returned in list
 */
export const SlackWorkspaceInfoSchema = z.object({
  /** Connection ID */
  id: z.string(),
  /** Workspace name */
  name: z.string(),
  /** Slack team ID */
  teamId: z.string(),
  /** Slack team name */
  teamName: z.string(),
  /** Whether Enterprise Grid */
  isEnterprise: z.boolean().optional(),
  /** Connection status */
  status: z.string(),
  /** When credentials were last validated */
  validatedAt: z.string().optional(),
  /** When token expires */
  tokenExpiresAt: z.string().optional()
});

/**
 * Response from listing workspaces
 */
export const ListSlackWorkspacesResponseSchema = z.object({
  workspaces: z.array(SlackWorkspaceInfoSchema)
});

// =============================================================================
// DELETE WORKSPACE
// =============================================================================

/**
 * Path vars for deleting a workspace connection
 */
export const DeleteSlackWorkspacePathVarsSchema = z.object({
  id: z.string().uuid()
});

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type ConnectSlackWorkspaceBody = z.infer<typeof ConnectSlackWorkspaceBodySchema>;
export type ConnectSlackWorkspaceResponse = z.infer<typeof ConnectSlackWorkspaceResponseSchema>;
export type SlackWorkspaceInfo = z.infer<typeof SlackWorkspaceInfoSchema>;
export type ListSlackWorkspacesResponse = z.infer<typeof ListSlackWorkspacesResponseSchema>;
